'use client'

import * as React from 'react'
import { cn } from '@shared/utils'
import { useFormField } from '../../hooks/useFormField'
import { useErrorHandling } from '../../utils/useErrorHandling'

/**
 * @group Components
 * 
 * @summary
 * <blockquote>Form message component that displays error messages for form fields.</blockquote>
 * 
 * @example
 * ```typescript
 * import { FormMessage } from '@shared/ui'
 * 
 * <FormMessage />
 * ```
 */
export const FormMessage = React.forwardRef<
  HTMLParagraphElement,
  React.ComponentPropsWithoutRef<'p'>
>(({ className, children, ...props }, ref) => {
  const field = useFormField()
  const { showContextError } = useErrorHandling()

  if (!field) {
    showContextError('FormMessage')
    return null
  }

  const { error, formMessageId } = field
  const body = error ? String(error?.message) : children

  if (!body) {
    return null
  }

  return (
    <p
      ref={ref}
      id={formMessageId}
      className={cn('text-sm font-medium text-destructive', className)}
      {...props}
    >
      {body}
    </p>
  )
})
FormMessage.displayName = 'FormMessage' 